import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import { Alert, Box, Button, Container, Skeleton, Typography } from '@mui/material';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import Api from '../../services/api';
import { Can } from '../../config/ability-context';
import CustomizedSnackbars from '../../components/material-snackbars';
import DialogSystemAlert from '../../components/dialog-system-alert';
import { getLastCalledsQueueByUserId } from '../../services/called.service';
import * as componentActions from '../../redux/actions/organizationcategoriesportal';
import * as lastCalledsQueueActions from '../../redux/actions/last-calleds-queue';
import * as userSessionActions from '../../redux/actions/user-session';
import PortalSearch from './components/portal-search';
import PortalOrganizations from './components/portal-organizations';
import { buildPortalCatalog } from './portal-catalog';
import { portalColors } from './portal-styles';

class PortalContent extends Component {
  state = {
    organizations: [],
    searchOptions: [],
    loading: true,
    error: false,
    dialogSystemAlert: false,
    dialogSystemAlertMessage: '',
    dialogSystemAlertName: '',
    notification: '',
  };

  componentDidMount() {
    this.loadCatalog();
    const user = this.props.userSession && this.props.userSession.user;
    if (user && user.id) {
      this.loadLastCalledsQueue(user.id);
      if (user.startpage === 'portal' && user.alert && user.alertalreadydisplayed === false) {
        this.setState({
          dialogSystemAlert: true,
          dialogSystemAlertMessage: user.alert.message,
          dialogSystemAlertName: user.alert.title,
        });
        this.alertTimer = setTimeout(this.closeSystemAlert, user.alert.time * 1000);
      }
    }
  }

  componentWillUnmount() {
    clearTimeout(this.alertTimer);
  }

  loadCatalog = async () => {
    this.setState({ loading: true, error: false });
    try {
      const result = await Api.get('/organizationcategories/portal/grouped');
      if (!result.data.success) throw new Error('Portal catalog request failed');
      const { organizations, searchOptions } = buildPortalCatalog(result.data.data || []);
      this.setState({
        organizations,
        searchOptions,
        loading: false,
      });
    } catch (error) {
      Api.kickoff(error);
      this.setState({ loading: false, error: true });
    }
  };

  selectOrganization = organizationid => {
    const params = new URLSearchParams(this.props.location.search);
    params.set('organization', organizationid);
    this.props.history.replace({ ...this.props.location, search: `?${params.toString()}` });
  };

  loadLastCalledsQueue = async userId => {
    const queue = this.props.lastCalledsQueue;
    if (queue && queue.items && Object.keys(queue.items).length > 0) return;
    try {
      const response = await getLastCalledsQueueByUserId(userId);
      if (response && response.success && Array.isArray(response.data)) {
        response.data.forEach(item => {
          if (item.id != null && item.title != null) {
            this.props.equeuelastCalledsQueue({ id: item.id, title: item.title });
          }
        });
      }
    } catch (error) {
      Api.kickoff(error);
      this.setState({ notification: this.props.intl.formatMessage({ id: 'process.error' }) });
    }
  };

  closeSystemAlert = () => {
    this.setState({ dialogSystemAlert: false });
    this.props.updateAlertAlreadyDisplayed(true);
  };

  openCategory = category => {
    this.props.addCurrent(category);
    this.props.history.push({
      pathname: `/${this.props.match.params.lang}/portal-category/${category.id}`,
      state: { action: 'cardReload', parentId: category.parentorganizationcategoryid },
    });
  };

  renderLoading = () => (
    <Box aria-busy="true" aria-label={this.props.intl.formatMessage({ id: 'portal.search.loading' })}
      sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '264px 1fr' }, gap: 5 }}>
      <Box sx={{ p: 2, bgcolor: portalColors.surface, borderRadius: '20px' }}>
        {[0, 1, 2].map(index => <Skeleton key={index} height={64} />)}
      </Box>
      <Box><Skeleton width="55%" height={44} />
        {[0, 1, 2].map(index => <Skeleton key={index} height={100} />)}
      </Box>
    </Box>
  );

  render() {
    const { intl } = this.props;
    const { organizations, searchOptions, loading, error } = this.state;
    const selectedOrganizationId = new URLSearchParams(this.props.location.search).get('organization');

    return (
      <Container component="main" maxWidth={false} sx={{ width: '100%', minWidth: 0, pt: 1, pb: { xs: 3, md: 5 }, px: { xs: 2, md: 4 } }}>
        <Box component="header" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          <Box>
            <Typography component="h1" variant="h4" sx={{ color: portalColors.ink, fontSize: { xs: 28, sm: 32 }, fontWeight: 500, lineHeight: 1.2, letterSpacing: '-0.035em' }}>
              {intl.formatMessage({ id: 'portal.title' })}
            </Typography>
            <Typography sx={{ mt: 1, fontSize: 16, lineHeight: 1.5, color: portalColors.muted }}>
              {intl.formatMessage({ id: 'portal.subtitle' })}
            </Typography>
          </Box>
          <Can I="List" a="OrganizationCategory">
            <Box sx={{ width: '100%', minWidth: 0, boxSizing: 'border-box', p: 2, bgcolor: portalColors.soft, borderRadius: '16px' }}>
              <PortalSearch options={searchOptions} onSelect={this.openCategory} loading={loading} />
            </Box>
          </Can>
        </Box>

        <Can I="List" a="OrganizationCategory">
          {loading ? this.renderLoading() : error ? (
            <Alert severity="error" action={
              <Button color="inherit" size="small" onClick={this.loadCatalog}>
                {intl.formatMessage({ id: 'portal.retry' })}
              </Button>
            }>
              {intl.formatMessage({ id: 'portal.load.error' })}
            </Alert>
          ) : organizations.length === 0 ? (
            <Box sx={{ py: 7, px: 2, textAlign: 'center', border: 1, borderColor: 'divider', borderRadius: 3 }}>
              <CategoryOutlinedIcon sx={{ fontSize: 38, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6">{intl.formatMessage({ id: 'portal.empty.title' })}</Typography>
              <Typography color="text.secondary">{intl.formatMessage({ id: 'portal.empty.description' })}</Typography>
            </Box>
          ) : (
            <PortalOrganizations organizations={organizations} selectedOrganizationId={selectedOrganizationId}
              onSelectOrganization={this.selectOrganization} />
          )}
        </Can>

        <DialogSystemAlert
          open={this.state.dialogSystemAlert}
          handleCloseDialogSystemAlert={this.closeSystemAlert}
          message={this.state.dialogSystemAlertMessage}
          title={this.state.dialogSystemAlertName}
        />
        <CustomizedSnackbars
          variant="error"
          message={this.state.notification}
          isOpen={Boolean(this.state.notification)}
          toClose={() => this.setState({ notification: '' })}
        />
      </Container>
    );
  }
}

const mapStateToProps = state => ({
  lastCalledsQueue: state.lastCalledsQueue,
  userSession: state.userSession,
});
const mapDispatchToProps = dispatch => bindActionCreators(
  Object.assign({}, componentActions, lastCalledsQueueActions, userSessionActions), dispatch
);
export default injectIntl(withRouter(connect(mapStateToProps, mapDispatchToProps)(PortalContent)));
